import { Navigate, useParams } from 'react-router-dom'
import { usePricingTier } from '@/hooks/use-pricing-tier'
import type { PricingLimits } from '@/hooks/use-pricing-tier'

/* ============================================================
   RequireFeature — Route guard for gated features
   Wraps a route and redirects to content page when feature
   is not available on the tenant's plan.
   ============================================================ */

interface RequireFeatureProps {
  feature: keyof PricingLimits
  children: React.ReactNode
}

export function RequireFeature({ feature, children }: RequireFeatureProps) {
  const { hasFeature, isLoading, requiredTier } = usePricingTier()
  const { slug } = useParams()

  // Wait for plan data to load before making gating decisions
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-t-transparent border-origin-accent" />
      </div>
    )
  }

  if (!hasFeature(feature)) {
    const required = requiredTier(feature)
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="mac-glass rounded-2xl p-8 text-center border border-white/[0.07] max-w-sm">
          <p className="text-3xl mb-3">🔒</p>
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            {String(feature).replace(/([A-Z])/g, ' $1').trim()}
          </h2>
          <p className="text-sm text-text-muted mb-4">
            This feature requires the{' '}
            <span className="text-origin-accent capitalize font-medium">{required}</span> plan.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-5 py-2 bg-white/5 border border-white/10 text-text-primary rounded-lg text-sm hover:bg-white/10 transition-colors mr-2"
          >
            Go Back
          </button>
          <Navigate to={`/${slug}/admin/content`} replace />
        </div>
      </div>
    )
  }

  return <>{children}</>
}
